import { readNavCityCoordsFromCookies } from "@/lib/navCityCookie";

const STORAGE_KEY = "petromin_nearest_location_coords_v1";
const SESSION_KEY = "petromin_nearest_location_session_v1";

/** Fired on `window` after persisted or session nearest-origin coords change. */
export const NEAREST_ORIGIN_CHANGED_EVENT = "petromin-nearest-origin-changed";

function dispatchNearestOriginChanged(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(NEAREST_ORIGIN_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

export type PersistedNearestCoords = {
  latitude: string;
  longitude: string;
};

export type GeolocationPermissionSnapshot =
  | "granted"
  | "denied"
  | "prompt"
  | "unknown";

export function isValidNearestCoordPair(lat: string, lng: string): boolean {
  const la = parseFloat(lat);
  const lo = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
  return la >= -90 && la <= 90 && lo >= -180 && lo <= 180;
}

function parseCoordsJson(raw: string): PersistedNearestCoords | null {
  try {
    const p = JSON.parse(raw) as unknown;
    if (!p || typeof p !== "object") return null;
    const o = p as Record<string, unknown>;
    const latitude =
      typeof o.latitude === "string"
        ? o.latitude.trim()
        : typeof o.latitude === "number" && Number.isFinite(o.latitude)
          ? String(o.latitude)
          : "";
    const longitude =
      typeof o.longitude === "string"
        ? o.longitude.trim()
        : typeof o.longitude === "number" && Number.isFinite(o.longitude)
          ? String(o.longitude)
          : "";
    if (!latitude || !longitude || !isValidNearestCoordPair(latitude, longitude)) {
      return null;
    }
    return { latitude, longitude };
  } catch {
    return null;
  }
}

/** Cross-tab / future visits — workstation picks and “always allow” geolocation. */
export function readPersistedNearestCoords(): PersistedNearestCoords | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return parseCoordsJson(raw);
  } catch {
    return null;
  }
}

/** Same tab only — “Allow this time” geolocation (not written to localStorage). */
export function readSessionNearestCoords(): PersistedNearestCoords | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return parseCoordsJson(raw);
  } catch {
    return null;
  }
}

/** Prefer durable storage, then session-only geolocation for this tab. */
export function readPreferredSearchOrigin(): PersistedNearestCoords | null {
  const persisted = readPersistedNearestCoords();
  if (persisted) return persisted;
  return readSessionNearestCoords();
}

/**
 * Workshop search origin: device GPS (persisted/session), then nav city cookie coords.
 */
export function resolveWorkshopSearchOrigin(): PersistedNearestCoords | null {
  return readPreferredSearchOrigin() ?? readNavCityCoordsFromCookies();
}

function clearSessionNearestCoords(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export type WritePersistedNearestCoordsOptions = {
  /**
   * When false, skip firing `NEAREST_ORIGIN_CHANGED_EVENT`. Use when the caller
   * updates search origin state in the same interaction (avoids listeners
   * clearing workshop lists / showing empty loading shells).
   * Default: true (Nav city change, granted geolocation, cross-tab sync).
   */
  notify?: boolean;
};

export function writePersistedNearestCoords(
  latitude: string,
  longitude: string,
  options?: WritePersistedNearestCoordsOptions
): void {
  if (typeof window === "undefined") return;
  const lat = latitude.trim();
  const lng = longitude.trim();
  if (!isValidNearestCoordPair(lat, lng)) return;
  const notify = options?.notify !== false;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ latitude: lat, longitude: lng })
    );
    clearSessionNearestCoords();
    if (notify) dispatchNearestOriginChanged();
  } catch {
    /* quota / private mode */
  }
}

export type WriteSessionNearestCoordsOptions = {
  /** Default true. Set false when the caller updates UI state in the same flow. */
  notify?: boolean;
};

export function writeSessionNearestCoords(
  latitude: string,
  longitude: string,
  options?: WriteSessionNearestCoordsOptions
): void {
  if (typeof window === "undefined") return;
  const lat = latitude.trim();
  const lng = longitude.trim();
  if (!isValidNearestCoordPair(lat, lng)) return;
  const notify = options?.notify !== false;
  try {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ latitude: lat, longitude: lng })
    );
    if (notify) dispatchNearestOriginChanged();
  } catch {
    /* quota / private mode */
  }
}

/**
 * Geolocation permission before calling `getCurrentPosition`.
 * - `granted`: site already allowed (e.g. “while using the site”) → may persist coords in localStorage.
 * - `prompt`: user will see a prompt → treat success as “this time” unless already `granted` (conservative).
 * - `denied`: skip requesting position.
 * - `unknown`: Permissions API missing/throws → session-only (do not assume persistent consent).
 */
export async function queryGeolocationPermission(): Promise<GeolocationPermissionSnapshot> {
  if (typeof navigator === "undefined") return "unknown";
  try {
    const q = navigator.permissions?.query;
    if (typeof q !== "function") return "unknown";
    const status = await q.call(navigator.permissions, {
      name: "geolocation" as PermissionName,
    });
    if (status.state === "granted") return "granted";
    if (status.state === "denied") return "denied";
    if (status.state === "prompt") return "prompt";
  } catch {
    /* Safari may throw for geolocation permission query */
  }
  return "unknown";
}

const DEFAULT_GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 12_000,
  maximumAge: 60_000,
};

export type RequestGeolocationPersistOptions = {
  /**
   * When false, skip `NEAREST_ORIGIN_CHANGED_EVENT` after writing coords (use when the
   * caller updates lat/lng state and refetches in the same interaction — avoids list wipe / flicker).
   */
  notify?: boolean;
};

/**
 * Reads permission, applies persist vs session policy, then calls `onSuccess` with coords.
 * Always invokes `onComplete` when finished (denied, error, invalid coords, or after success).
 */
export function requestGeolocationWithPersistPolicy(
  onSuccess: (latitude: string, longitude: string) => void,
  onComplete: () => void,
  geoOptions: PositionOptions = DEFAULT_GEO_OPTIONS,
  persistOptions?: RequestGeolocationPersistOptions
): void {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    queueMicrotask(onComplete);
    return;
  }
  const notifyWrites = persistOptions?.notify !== false;
  void (async () => {
    let permissionBefore: GeolocationPermissionSnapshot = "unknown";
    try {
      permissionBefore = await queryGeolocationPermission();
    } catch {
      permissionBefore = "unknown";
    }
    if (permissionBefore === "denied") {
      onComplete();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const la = String(pos.coords.latitude);
        const lo = String(pos.coords.longitude);
        if (!isValidNearestCoordPair(la, lo)) {
          onComplete();
          return;
        }
        if (permissionBefore === "granted") {
          writePersistedNearestCoords(la, lo, { notify: notifyWrites });
        } else {
          writeSessionNearestCoords(la, lo, { notify: notifyWrites });
        }
        onSuccess(la, lo);
        onComplete();
      },
      () => onComplete(),
      geoOptions
    );
  })();
}
